import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useSystemConfigs, useUpdateSystemConfig } from '../../hooks/useMovie';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminConfigPage() {
  usePageTitle('Platform Config · Kast');
  const { data: configs = [], isLoading, error } = useSystemConfigs();
  const updateMutation = useUpdateSystemConfig();
  const [editing, setEditing] = useState({}); // key → value being edited

  const startEdit = (key, currentValue) => {
    setEditing((e) => ({ ...e, [key]: currentValue }));
  };

  const cancelEdit = (key) => {
    setEditing((e) => { const n = { ...e }; delete n[key]; return n; });
  };

  const saveEdit = async (key, description) => {
    const value = editing[key];
    if (value === undefined) return;
    try {
      await updateMutation.mutateAsync({ key, value, description });
      toast.success(`"${key}" updated.`);
      cancelEdit(key);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Update failed'));
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
          <Icon icon="ph:gear-six-bold" className="size-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-foreground">System Configuration</h1>
          <p className="text-xs text-muted-foreground">These key-value pairs control global application behaviour</p>
        </div>
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load configuration.')}</EmptyState>
      ) : configs.length === 0 ? (
        <EmptyState icon="ph:gear-six-bold">No configuration entries yet</EmptyState>
      ) : (
        <div className="flex flex-col gap-3">
          {configs.map((c) => (
            <div
              key={c.Key}
              className="flex flex-col gap-3 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06] sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <code className="text-sm font-semibold text-foreground">{c.Key}</code>
                {c.Description && <p className="mt-0.5 text-xs text-muted-foreground">{c.Description}</p>}
                <p className="mt-0.5 text-[11px] text-muted-foreground/60">Last updated: {c.LastUpdated?.split('T')[0]}</p>
              </div>

              {editing[c.Key] !== undefined ? (
                <div className="flex shrink-0 items-center gap-2">
                  <input
                    className={`${inputClass} sm:w-56`}
                    value={editing[c.Key]}
                    onChange={(e) => setEditing((en) => ({ ...en, [c.Key]: e.target.value }))}
                    autoFocus
                  />
                  <Button
                    size="sm"
                    onClick={() => saveEdit(c.Key, c.Description)}
                    disabled={updateMutation.isPending}
                    className="gap-1.5"
                  >
                    <Icon icon="ph:floppy-disk-bold" />
                    Save
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => cancelEdit(c.Key)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex shrink-0 items-center gap-3">
                  <code className="rounded-md bg-primary/10 px-2.5 py-1 text-sm font-semibold text-primary ring-1 ring-primary/20">{c.Value}</code>
                  <Button size="sm" variant="secondary" onClick={() => startEdit(c.Key, c.Value)} className="gap-1.5">
                    <Icon icon="ph:pencil-simple-bold" />
                    Edit
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
